import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { OBTAIN_LMS_TOKEN } from "../../Queries";
import { Course } from "../../types";

export interface GradePushModalProps {
  course: Course;
  onClose: () => void;
  onConnected: () => void;
  onSkip: () => void;
  context?: "push" | "send" | "schedule";
}

export const GradePushModal: React.FC<GradePushModalProps> = ({
  course,
  onClose,
  onConnected,
  onSkip,
  context = "push",
}) => {
  const [t] = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [obtainLmsToken] = useMutation(OBTAIN_LMS_TOKEN);
  const handledRef = useRef(false);

  const handleOAuthSuccess = useCallback(
    async (code: string, state: string) => {
      setLoading(true);
      setError(null);
      try {
        await obtainLmsToken({
          variables: { code, state },
          context: { debounceKey: "obtainLmsToken" },
        });
        onConnected();
        onClose();
      } catch (e) {
        setError(t("modals.gradePushModal.connectError"));
      } finally {
        setLoading(false);
      }
    },
    [obtainLmsToken, onConnected, onClose, t],
  );

  const handleOAuthMessage = useCallback(
    (event: MessageEvent<any>) => {
      if (handledRef.current) return;
      const data = event.data as any;
      if (!data || typeof data !== "object") return;
      // Optional: restrict to same-origin messages
      if (event.origin && event.origin !== window.location.origin) return;
      if (data.type === "OAUTH_SUCCESS") {
        handledRef.current = true;
        window.removeEventListener("message", handleOAuthMessage as any);
        if (data.platform === "moodle") {
          onConnected();
          onClose();
          return;
        }
        if (data.code && data.state) {
          handleOAuthSuccess(data.code, data.state);
        }
      } else if (data.type === "OAUTH_ERROR") {
        handledRef.current = true;
        window.removeEventListener("message", handleOAuthMessage as any);
        setError(data.error || t("modals.gradePushModal.oauthError"));
      }
    },
    [handleOAuthSuccess, onConnected, onClose, t],
  );

  useEffect(() => {
    window.addEventListener("message", handleOAuthMessage as any);
    return () => {
      window.removeEventListener("message", handleOAuthMessage as any);
    };
  }, [handleOAuthMessage]);

  function openOAuth() {
    setError(null);
    if (!course?.oauthUrl) return;
    window.open(
      course.oauthUrl,
      `${course.lmsType || "LMS"} Connect`,
      `dependent=${1}, alwaysOnTop=${1}, width=${800}, height=${450}`,
    );
  }

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>
        {context === "push"
          ? t("modals.gradePushModal.pushTitle")
          : context === "send"
            ? t("modals.gradePushModal.sendTitle")
            : t("modals.gradePushModal.scheduleTitle")}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <DialogContentText>
          {context === "push" && t("modals.gradePushModal.pushBody")}
          {context === "send" && t("modals.gradePushModal.sendBody")}
          {context === "schedule" && t("modals.gradePushModal.scheduleBody")}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onClose();
            onSkip();
          }}
        >
          {t("modals.gradePushModal.skip")}
        </Button>
        <Button onClick={openOAuth} disabled={loading} autoFocus>
          {loading
            ? t("modals.gradePushModal.connecting")
            : t("modals.gradePushModal.login", {
                lmsType:
                  course.lmsType || t("modals.gradePushModal.lmsFallback"),
              })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
